import React, { createContext, useContext, useRef, useState } from 'react';

import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

type TutorialContextType = {
  isVisibleForChat: boolean;
  setIsVisibleForChat: React.Dispatch<React.SetStateAction<boolean>>;
  showOverlayForChat: boolean;
  setShowOverlayForChat: React.Dispatch<React.SetStateAction<boolean>>;
  headerOverlayActiveForChat: boolean;
  setHeaderOverlayActiveForChat: React.Dispatch<React.SetStateAction<boolean>>;
  isFromChatList: boolean;
  setIsFromChatList: React.Dispatch<React.SetStateAction<boolean>>;
  chatTutorialWebState: string | null;
  setChatTutorialWebStateAndInStorage: (state: string) => void;
  chatHeaderHeight: number;
  setChatHeaderHeight: React.Dispatch<React.SetStateAction<number>>;
  tooltipPopupHeight: number;
  setTooltipPopupHeight: React.Dispatch<React.SetStateAction<number>>;
};

const ChatTutorialWebContext = createContext<TutorialContextType | undefined>(
  undefined
);

export const ChatTutorialWebProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isVisibleForChat, setIsVisibleForChat] = useState(false);
  const [showOverlayForChat, setShowOverlayForChat] = useState(false);
  const { trackEvent } = useMixpanel();
  const hasTrackedOnboardingStart = useRef(false);
  const [isFromChatList, setIsFromChatList] = useState(false);
  const [headerOverlayActiveForChat, setHeaderOverlayActiveForChat] =
    useState<boolean>(false);
  const [chatHeaderHeight, setChatHeaderHeight] = useState<number>(0);
  const [tooltipPopupHeight, setTooltipPopupHeight] = useState<number>(0);

  const [chatTutorialWebState, setChatTutorialWebState] = useState<
    string | null
  >('');

  const setChatTutorialWebStateAndInStorage = async (state: string) => {
    const previousState = chatTutorialWebState;
    // Set React state first so consumers see the new value synchronously
    // (in the same commit). Awaiting AsyncStorage.setItem first caused a
    // race where ChatForm's setTagsCollection([]) re-rendered with the OLD
    // tutorial state, a 500ms timer scheduled off that stale value rolled
    // the tutorial BACK, and only then did the storage write resolve and
    // the real state update land. See TA-9532.
    setChatTutorialWebState(state);
    await AsyncStorage.setItem('chatTutorialWebState', state);

    if (previousState !== state) {
      if (
        !hasTrackedOnboardingStart.current &&
        (previousState === '' || previousState === null) &&
        state === '0'
      ) {
        hasTrackedOnboardingStart.current = true;
        trackEvent(MIXPANEL_EVENTS.ONBOARDING_STARTED, {
          platform: 'web',
          tutorial_type: 'chat',
        });
      }

      trackEvent(MIXPANEL_EVENTS.ONBOARDING_STEP_COMPLETED, {
        platform: 'web',
        tutorial_type: 'chat',
        from_state: previousState,
        to_state: state,
      });

      if (state === '9') {
        trackEvent(MIXPANEL_EVENTS.ONBOARDING_COMPLETED, {
          platform: 'web',
          tutorial_type: 'chat',
          final_state: state,
        });
      }
    }
  };

  return (
    <ChatTutorialWebContext.Provider
      value={{
        isVisibleForChat,
        setIsVisibleForChat,
        showOverlayForChat,
        setShowOverlayForChat,
        headerOverlayActiveForChat,
        setHeaderOverlayActiveForChat,
        isFromChatList,
        setIsFromChatList,
        chatTutorialWebState,
        setChatTutorialWebStateAndInStorage,
        chatHeaderHeight,
        setChatHeaderHeight,
        tooltipPopupHeight,
        setTooltipPopupHeight,
      }}>
      {children}
    </ChatTutorialWebContext.Provider>
  );
};

// Hook to use context
export function useChatTutorialWeb() {
  const context = useContext(ChatTutorialWebContext);
  if (context === undefined) {
    throw new Error(
      'useChatTutorialWeb must be used within a ChatTutorialWebProvider'
    );
  }
  return context;
}
